"use client";

import { useState, useEffect, useRef, useCallback } from "react";
import { Button, GearIcon, SunIcon, MoonIcon, SpeakerOnIcon, SpeakerOffIcon, CRTIcon, CRTOffIcon } from "@/components/ui/Button";
import { Tooltip } from "@/components/ui/Tooltip";
import { useThemeMode } from "@/context/ThemeContext";
import { useAudio } from "@/context/AudioContext";

const devToolClassName = "text-[var(--toggle-icon)] !bg-transparent !backdrop-blur-none hover:!bg-[rgba(var(--surface-rgb),0.6)] hover:!backdrop-blur-xl transition-all";

interface ToggleTrayProps {
  crtEnabled: boolean;
  onCrtToggle: () => void;
}

export function ToggleTray({ crtEnabled, onCrtToggle }: ToggleTrayProps) {
  const [isOpen, setIsOpen] = useState(false);
  const trayRef = useRef<HTMLDivElement>(null);
  const gearRef = useRef<HTMLButtonElement | HTMLAnchorElement>(null);
  const { mode, toggleMode } = useThemeMode();
  const { isMuted, setMuted } = useAudio();
  const isLightMode = mode === "light";

  const close = useCallback(() => setIsOpen(false), []);

  // Escape key closes tray. If focus was on one of the toggles, hand it back to
  // the gear — the toggles go inert when collapsed, which would otherwise drop
  // focus to <body>.
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (trayRef.current?.contains(document.activeElement)) gearRef.current?.focus();
      close();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [isOpen, close]);

  // Click outside closes tray
  useEffect(() => {
    if (!isOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (trayRef.current && !trayRef.current.contains(e.target as Node)) {
        close();
      }
    };
    // Use timeout to avoid the opening click triggering close
    const id = setTimeout(() => {
      window.addEventListener("click", handleClick);
    }, 0);
    return () => {
      clearTimeout(id);
      window.removeEventListener("click", handleClick);
    };
  }, [isOpen, close]);

  const toggleItems = [
    {
      key: "theme",
      label: isLightMode ? "Dark mode" : "Light mode",
      ariaLabel: isLightMode ? "Switch to dark mode" : "Switch to light mode",
      icon: isLightMode ? <MoonIcon size={14} /> : <SunIcon size={14} />,
      onClick: toggleMode,
    },
    {
      key: "audio",
      label: isMuted ? "Audio on" : "Audio off",
      ariaLabel: isMuted ? "Turn audio on" : "Turn audio off",
      icon: isMuted ? <SpeakerOffIcon size={14} /> : <SpeakerOnIcon size={14} />,
      onClick: () => setMuted(!isMuted),
    },
    {
      key: "crt",
      label: crtEnabled ? "CRT off" : "CRT on",
      ariaLabel: crtEnabled ? "Turn CRT effects off" : "Turn CRT effects on",
      icon: crtEnabled ? <CRTIcon size={14} /> : <CRTOffIcon size={14} />,
      onClick: onCrtToggle,
    },
  ];

  return (
    <div ref={trayRef} className="fixed bottom-4 left-4 z-[250] flex flex-col items-center gap-1 pointer-events-none [&>*]:pointer-events-auto">
      {/* Toggle items - fan out above gear */}
      {toggleItems.map((item, i) => {
        // Reverse index so top item animates last on open (bottom-up stagger)
        const staggerIndex = toggleItems.length - 1 - i;
        return (
          <div
            key={item.key}
            // Collapsed toggles sit at opacity 0: keep them out of the tab order and
            // the a11y tree until the tray is open (no invisible focus stops).
            inert={!isOpen}
            style={{
              opacity: isOpen ? 1 : 0,
              transform: isOpen ? "translateY(0)" : "translateY(8px)",
              pointerEvents: isOpen ? "auto" : "none",
              transition: "opacity 200ms ease, transform 200ms ease",
              transitionDelay: isOpen ? `${staggerIndex * 50}ms` : "0ms",
            }}
          >
            <Tooltip text={item.label} position="right">
              <Button
                variant="icon-sm"
                magnetic={false}
                onClick={item.onClick}
                aria-label={item.ariaLabel}
                className={devToolClassName}
              >
                {item.icon}
              </Button>
            </Tooltip>
          </div>
        );
      })}

      {/* Gear trigger - always visible */}
      <Tooltip text={isOpen ? "Close settings" : "Settings"} position="right">
        <Button
          variant="icon-sm"
          magnetic={false}
          ref={gearRef}
          onClick={() => setIsOpen(!isOpen)}
          aria-label={isOpen ? "Close settings" : "Open settings"}
          aria-expanded={isOpen}
          className={devToolClassName}
        >
          <span
            style={{
              display: "inline-flex",
              transform: isOpen ? "rotate(90deg)" : "rotate(0deg)",
              transition: "transform 300ms ease",
            }}
          >
            <GearIcon size={14} />
          </span>
        </Button>
      </Tooltip>
    </div>
  );
}
